import React from 'react';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import find from 'lodash/find';
import isArrayLike from 'lodash/isArrayLike';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { nanoid } from 'nanoid';
import MUIDataTable from 'mui-datatables';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Paper, Tooltip, Chip, Grid,
} from '@mui/material';
import IPhoneIcon from '@mui/icons-material/PhoneIphone';

import ProfilePhoto from '../common/profile-photo';
import AppCenterButtons from '../common/app-center-buttons';
import UserEditForm from './user-edit-form';
import CustomToolbar from './custom-toolbar';
import withPersistentSettings from '../common/table-with-persistent-settings';
import {
  shouldFetchList,
  WRANGLER_ACTIVE,
  getUsersAppCenterLists,
  IOS,
  BOTH_APPS,
  SMALL_PROFILE_PHOTO_SIZE,
  getMarketName,
} from '../../lib/utils';
import { convertImageUrlToPublicId } from '../../lib/cloudinary';
import { isAMatchOrPhoneMatch, customFilterForMarket } from '../../lib/table-utils';
import { ACTIONS, FEATURES } from '../../access';

import wranglerActions from '../../actions/wranglers';
import taskActions from '../../actions/tasks';
import * as snackbarActions from '../../actions/snackbar';
import WrappedSpinner from '../common/wrapped-spinner';

const styles = {
  root: {
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'auto',
  },
};

const IS_VERIFIED = 'Verified';
const IS_NOT_VERIFIED = 'Not verified';
const FIRST_NAME = 'First Name';
const LAST_NAME = 'Last Name';
const PHONE = 'Phone';
const STATUS = 'Status';
const APP_ACCESS = 'App Access';

class UserTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isFormOpen: false,
      editWrangler: { },
      editing: false,
    };

    this.handleCloseForm = this.handleCloseForm.bind(this);
    this.handleSaveForm = this.handleSaveForm.bind(this);
    this.handleAppCenterAction = this.handleAppCenterAction.bind(this);
    this.handleEditWrangler = this.handleEditWrangler.bind(this);
    this.handleAddWrangler = this.handleAddWrangler.bind(this);
  }

  componentDidMount() {
    const { fetchWranglers, shouldFetchWranglers, match } = this.props;
    const marketId = get(match, 'params.marketId');

    if (marketId) {
      if (shouldFetchWranglers) fetchWranglers({ market_id: marketId });
    }
  }

  get wranglers() {
    const wranglers = get(this, 'props.wranglers', []);

    return sortBy(
      wranglers,
      (wrangler) => wrangler.lastName.toLowerCase(),
    );
  }

  get data() {
    const { markets = [] } = this.props;
    const marketsById = keyBy(markets, '_id');

    const data = this.wranglers.map((wrangler) => {
      const isVerified = wrangler.isVerified
        ? IS_VERIFIED
        : IS_NOT_VERIFIED;
      const tableData = [
        get(wrangler, '_id', ''),
        get(wrangler, 'firstName', ''),
        get(wrangler, 'lastName', ''),
        get(wrangler, 'profilePhoto.url', ''),
        get(wrangler, 'phoneNumber', ''),
        get(wrangler, 'status', ''),
        get(wrangler, 'role', ''),
        getUsersAppCenterLists(wrangler),
        get(wrangler, 'marketIds', []).map((mktId) => getMarketName(get(marketsById, mktId, {}))),
        isVerified,
      ];
      return tableData;
    });
    return data;
  }

  get tableOptions() {
    const { tableOptions, match, currentWrangler } = this.props;
    const { marketId } = get(match, 'params');
    const basicOptions = tableOptions;
    let hasAccess;
    if (isUndefined(currentWrangler)) {
      hasAccess = () => false;
    } else {
      hasAccess = currentWrangler.access.can;
    }

    const showAppCenterButtons = hasAccess(ACTIONS.READ, FEATURES.APP_CENTER_BUTTONS);
    if (marketId && showAppCenterButtons) basicOptions.selectableRows = 'multiple';
    if (showAppCenterButtons) {
      basicOptions.customToolbarSelect = (selectedRowObjects) => {
        const { data } = selectedRowObjects;

        const selectedRows = data.map((item) => item.dataIndex);
        const selectedWranglers = this.wranglers.filter(
          (wrangler, index) => wrangler.email && selectedRows.includes(index),
        );
        const selectedEmails = selectedWranglers.map((report) => report.email);
        return (
          <AppCenterButtons
            emails={selectedEmails}
            showIOSAdd
            showIOSEdit
          />
        );
      };
    }

    basicOptions.customSearch = (searchQuery, currentRow) => (
      isAMatchOrPhoneMatch(searchQuery, currentRow)
    );
    basicOptions.onRowClick = (rowData) => {
      this.handleEditWrangler(rowData[0]);
    };

    basicOptions.customToolbar = () => (
      <CustomToolbar onClick={this.handleAddWrangler} type="Add" />
    );

    return basicOptions;
  }

  get columns() {
    const { selectedFilters, primarySort, addToColumnData } = this.props;

    // the order of objects in columnData needs to match
    // the order of objects in data - both are referenced
    // on the render of the MUI datatable
    // columnData[0] is hidden from view, but contains the userId
    // and is used to allow the onRowClick above to know what user
    // it is referencing

    const columnData = [
      {
        name: 'ID',
        options: {
          display: 'excluded',
          filter: false,
        },
      },
      {
        name: FIRST_NAME,
        options: {
          customFilterListOptions: {
            render: (v) => `${FIRST_NAME}: ${v}`,
          },
        },
      },
      {
        name: LAST_NAME,
        options: {
          customFilterListOptions: {
            render: (v) => `${LAST_NAME}: ${v}`,
          },
        },
      },
      {
        name: 'Profile Picture',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value) => {
            if (value) {
              return (
                <ProfilePhoto
                  publicId={convertImageUrlToPublicId(value)}
                  size={SMALL_PROFILE_PHOTO_SIZE}
                />
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: PHONE, // note: customSearch in tableOption needs to match index of phoneNumber in columnData array.
        options: {
          customFilterListOptions: {
            render: (v) => `${PHONE}: ${v}`,
          },
        },
      },
      {
        name: STATUS,
        options: {
          customFilterListOptions: {
            render: (v) => `${STATUS}: ${v}`,
          },
        },
      },
      {
        name: 'Role',
        options: {
          customFilterListOptions: {
            render: (v) => `Role: ${v}`,
          },
        },
      },
      {
        name: APP_ACCESS,
        options: {
          customFilterListOptions: {
            render: (v) => `${APP_ACCESS}: ${v}`,
          },
          customBodyRender: (value) => {
            const iosIcon = (
              <Tooltip title={IOS}>
                <IPhoneIcon />
              </Tooltip>
            );
            if (value === IOS) {
              return iosIcon;
            } else if (value === BOTH_APPS) {
              return (
                <Grid container direction="row">
                  <Grid item xs={6}>
                    {iosIcon}
                  </Grid>
                </Grid>
              );
            } else {
              return value;
            }
          },
        },
      },
      {
        name: 'Markets',
        options: {
          sort: false,
          customBodyRender: (marketNames) => marketNames.filter(Boolean).map((marketName) => (
            <Chip
              variant="outlined"
              label={marketName}
              key={marketName}
              color="primary"
            />
          )),
          filterOptions: {
            logic: (marketIds, filters) => customFilterForMarket(marketIds, filters),
          },
        },
      },
      {
        name: 'Verified',
        options: {
          customBodyRender: (value) => {
            const verifiedIcon = value === IS_VERIFIED ? <DoneIcon /> : <ErrorIcon style={{ color: 'red' }} />;
            const verifiedString = value === IS_VERIFIED ? 'Verified' : 'Verify by logging into FieldApp';
            return (
              <Tooltip title={verifiedString}>
                {verifiedIcon}
              </Tooltip>
            );
          },
        },
      },
    ];

    return addToColumnData(columnData, selectedFilters, primarySort);
  }

  handleCloseForm() {
    this.setState({ isFormOpen: false, editing: false, editWrangler: {} });
  }

  handleAddWrangler() {
    this.setState({
      editWrangler: {
        _id: nanoid(24),
        status: WRANGLER_ACTIVE,
        isVerified: false,
      },
      isFormOpen: true,
      editing: false,
    });
  }

  handleEditWrangler(data) {
    const { wranglers } = this.props;

    const matchingWrangler = find(wranglers, (wrangler) => wrangler._id === data);
    this.setState({
      editWrangler: matchingWrangler,
      isFormOpen: true,
      editing: true,
    });
  }

  handleAppCenterAction() {
    const {
      match,
      fetchWranglers,
    } = this.props;

    const { marketId } = get(match, 'params');
    const query = marketId ? { market_id: marketId } : undefined;

    const stateUpdate = {
      isFormOpen: false,
      editing: false,
      editWrangler: {},
    };
    this.setState(stateUpdate, () => {
      fetchWranglers(query);
    });
  }

  handleSaveForm(wrangler, editing) {
    const {
      match,
      fetchWranglers,
      updateWrangler,
      createWrangler,
      showSuccessSnackbar,
      showErrorSnackbar,
    } = this.props;
    const { marketId } = get(match, 'params');
    const query = marketId ? { market_id: marketId } : undefined;
    const saveMethod = editing ? updateWrangler : createWrangler;
    const wranglerId = editing ? wrangler._id : undefined;

    saveMethod(wrangler, wranglerId)
      .then(({ data }) => {
        const stateUpdate = {
          isFormOpen: false,
          editing: false,
          editWrangler: {},
        };
        this.setState(stateUpdate, () => {
          showSuccessSnackbar(`Success ${editing ? 'updating' : 'creating'} wrangler ${get(data, 'firstName')} ${get(data, 'lastName')}`);
        });
      })
      .then(() => fetchWranglers(query))
      .catch((error) => {
        showErrorSnackbar(`Error ${editing ? 'updating' : 'creating'}: ${get(error, 'response.data.message')}`);
      });
  }

  render() {
    const { isLoading, getTitleFromData } = this.props;
    const { editWrangler, editing, isFormOpen } = this.state;
    const displayData = this.data;

    return (
      <WrappedSpinner isLoading={isLoading}>
        <Paper style={styles.root} sx={{ mt: 3 }}>
          <MUIDataTable
            title={getTitleFromData('Field Workers', displayData)}
            columns={this.columns}
            data={displayData}
            options={this.tableOptions}
          />
          {isFormOpen && !isEmpty(editWrangler) && (
            <UserEditForm
              key={`wrangler-edit-form-${editWrangler._id}`}
              isOpen={isFormOpen}
              onCancel={this.handleCloseForm}
              onSave={this.handleSaveForm}
              onAppCenterClick={this.handleAppCenterAction}
              wrangler={editWrangler}
              editing={editing}
            />
          )}
        </Paper>
      </WrappedSpinner>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...wranglerActions,
  ...taskActions,
  ...snackbarActions,
}, dispatch);

const mapStateToProps = ({ markets, wranglers }, { wranglers: propWranglers }) => ({
  markets: markets.markets,
  isLoading: wranglers.isLoading || markets.isLoading,
  wranglers: propWranglers || wranglers.marketWranglers,
  currentWrangler: wranglers.currentWrangler,
  shouldFetchWranglers: !isArrayLike(propWranglers) || shouldFetchList(wranglers),
});

export default withPersistentSettings(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(UserTable)),
  'UserTable',
);
